import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import AvatarImg from "./avatar.jpg";
import { motion, useAnimation } from "framer-motion";
import Social from "../Social";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  fontStyle: {
    fontWeight: "bold",
    color: "#ffb510",
  },
  fontStyleText: {
    fontWeight: "bold",
    color: "#fff",
  },
}));

const AboutMobile = () => {
  const classes = useStyles();
  const controls = useAnimation();
  const [hoveredSkill, setHoveredSkill] = useState(null);

  const skills = [
    {
      name: "React.js",
      level: 80,
      color: "#61DAFB",
      description:
        "Advanced React development with hooks, context, and modern patterns",
    },
    {
      name: "Node.js",
      level: 70,
      color: "#339933",
      description: "Backend development with Express.js and RESTful APIs",
    },
    {
      name: "MongoDB",
      level: 80,
      color: "#47A248",
      description: "Database design and NoSQL query optimization",
    },
    {
      name: "Angular",
      level: 75,
      color: "#DD0031",
      description: "Component-based architecture and TypeScript development",
    },
    {
      name: "Next.js",
      level: 85,
      color: "#000000",
      description: "SSR, SSG, and full-stack React applications",
    },
    {
      name: "ASP.NET Core",
      level: 70,
      color: "#512BD4",
      description: "C# backend development and API creation",
    },
  ];

  useEffect(() => {
    controls.start((i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: "easeOut",
      },
    }));
  }, [controls]);

  return (
    <>
      <Social />

      <Container maxWidth="sm" sx={{ minHeight: "100vh", py: 2 }}>
        <motion.div animate={controls} custom={0}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              gap: 3,
              px: 2,
            }}
          >
            {/* Profile Section */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              style={{ width: "100%" }}
            >
              <Card
                sx={{
                  background: "linear-gradient(135deg, #1a1a2e, #16213e)",
                  border: "1px solid rgba(255, 181, 16, 0.2)",
                  borderRadius: "20px",
                  overflow: "hidden",
                  width: "100%",
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.3)",
                }}
              >
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8, duration: 0.6, type: "spring" }}
                    style={{ marginBottom: "15px" }}
                  >
                    <motion.img
                      src={AvatarImg}
                      style={{
                        width: "150px",
                        height: "150px",
                        borderRadius: "50%",
                        border: "3px solid #ffb510",
                        objectFit: "cover",
                      }}
                      whileHover={{
                        scale: 1.05,
                        rotate: 3,
                        boxShadow: "0 0 25px rgba(255, 181, 16, 0.5)",
                      }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.6 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: "#ffb510",
                        mb: 1,
                        fontWeight: "bold",
                        background: "linear-gradient(45deg, #ffb510, #ff6b35)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontSize: { xs: "1.5rem", sm: "1.8rem" },
                      }}
                    >
                      Muhammad Humail Saleem
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        color: "#fff",
                        mb: 2,
                        opacity: 0.9,
                        fontWeight: 300,
                        fontSize: { xs: "1rem", sm: "1.2rem" },
                      }}
                    >
                      Full Stack Developer
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        lineHeight: 1.6,
                        mb: 2,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      }}
                    >
                      Passionate developer with 3+ years of experience building
                      innovative web applications. Currently working at{" "}
                      <motion.a
                        href="https://raaweek12.com/"
                        target="_blank"
                        style={{
                          color: "#ffb510",
                          textDecoration: "none",
                          fontWeight: "bold",
                        }}
                        whileHover={{ scale: 1.05 }}
                      >
                        RaaWee K12 Inc.
                      </motion.a>
                    </Typography>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Skills Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              style={{ width: "100%" }}
            >
              <Grid container spacing={2}>
                {skills.map((skill, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 1.4 + index * 0.1,
                        duration: 0.6,
                        type: "spring",
                        stiffness: 100,
                      }}
                      whileHover={{
                        scale: 1.05,
                        y: -5,
                        transition: { duration: 0.2 },
                      }}
                      whileTap={{ scale: 0.95 }}
                      onHoverStart={() => setHoveredSkill(index)}
                      onHoverEnd={() => setHoveredSkill(null)}
                    >
                      <Card
                        sx={{
                          background:
                            hoveredSkill === index
                              ? `linear-gradient(135deg, ${skill.color}20, ${skill.color}10)`
                              : "linear-gradient(135deg, #1a1a2e, #16213e)",
                          border: `2px solid ${
                            hoveredSkill === index
                              ? skill.color
                              : "rgba(255, 181, 16, 0.2)"
                          }`,
                          borderRadius: "15px",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          height: "100%",
                          position: "relative",
                          overflow: "hidden",
                          minHeight: "140px",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(45deg, ${skill.color}10, transparent)`,
                            opacity: hoveredSkill === index ? 1 : 0,
                            transition: "opacity 0.3s ease",
                            zIndex: 1,
                          },
                        }}
                      >
                        <CardContent
                          sx={{ p: 2, position: "relative", zIndex: 2 }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: skill.color,
                              mb: 1.5,
                              fontWeight: "bold",
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              textAlign: "center",
                            }}
                          >
                            {skill.name}
                          </Typography>

                          <div
                            style={{
                              width: "100%",
                              height: "8px",
                              backgroundColor: "rgba(255,255,255,0.1)",
                              borderRadius: "4px",
                              overflow: "hidden",
                              marginBottom: "0.8rem",
                              position: "relative",
                            }}
                          >
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${skill.level}%` }}
                              transition={{
                                delay: 1.6 + index * 0.1,
                                duration: 1.2,
                                ease: "easeOut",
                              }}
                              style={{
                                height: "100%",
                                background: `linear-gradient(90deg, ${skill.color}, ${skill.color}80)`,
                                borderRadius: "4px",
                                position: "relative",
                                overflow: "hidden",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background:
                                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                                animation: "shimmer 2s infinite",
                              }}
                            />
                          </div>

                          <Typography
                            variant="caption"
                            sx={{
                              color: "#fff",
                              opacity: 0.9,
                              fontSize: { xs: "0.7rem", sm: "0.8rem" },
                              textAlign: "center",
                              fontWeight: 500,
                              display: "block",
                            }}
                          >
                            {skill.level}%
                          </Typography>

                          {hoveredSkill === index && (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.2 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#fff",
                                  opacity: 0.8,
                                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  display: "block",
                                  mt: 1,
                                  textAlign: "center",
                                  lineHeight: 1.3,
                                }}
                              >
                                {skill.description}
                              </Typography>
                            </motion.div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>
        </motion.div>
      </Container>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </>
  );
};

export default AboutMobile;
